/**
 * Export Pro Calculation Section Component
 *
 * @description Full section for Advanced Calculator CSV export with informational alerts
 * @version 1.0.0
 * @created 2025-11-03
 */

import { useState } from 'react';
import {
  Paper,
  Stack,
  Text,
  Button,
  Alert,
  Group,
} from '@mantine/core';
import {
  IconFileDownload,
  IconInfoCircle,
  IconAlertTriangle,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import type { ProCalculatorFormValues, ProCalculatorResults } from '../../../../types/proCalculator';
import { generateProCalculationCSV } from '../../../../utils/csv/generateProCalculationCSV';
import { downloadCalculationCSV } from '../../../../utils/csv/downloadCSV';

/**
 * Component Props
 */
interface ExportProCalculationSectionProps {
  /** Advanced Calculator form values */
  formValues: ProCalculatorFormValues;

  /** Advanced Calculator results */
  results: ProCalculatorResults;
}

/**
 * Export Pro Calculation Section Component
 *
 * Provides a dedicated section for exporting Advanced Calculator data to CSV.
 * Includes informational alerts explaining CSV contents and a primary export button.
 *
 * Features:
 * - Informational alert about CSV contents
 * - Security warning about data privacy
 * - Loading state during generation
 * - Success/error notifications
 * - Mobile-responsive design
 */
export function ExportProCalculationSection({
  formValues,
  results,
}: ExportProCalculationSectionProps) {
  const [loading, setLoading] = useState(false);

  /**
   * Handle CSV export with notifications
   */
  const handleExport = async () => {
    try {
      setLoading(true);

      // GA4: Track export button click
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'export_pro_calculation_csv', {
          location: 'pro_calculator',
          year: formValues.calculationYear,
        });
      }

      // Generate CSV
      const csv = generateProCalculationCSV(formValues, results);

      // Download CSV
      downloadCalculationCSV(csv, formValues.calculationYear);

      // GA4: Track successful download
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'csv_download_success', {
          calculator_type: 'professional',
          file_size: csv.length,
          year: formValues.calculationYear,
        });
      }

      // Show success notification
      notifications.show({
        title: 'Sukces',
        message: 'Plik CSV z szczegółową kalkulacją został pobrany',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
    } catch (error) {
      // GA4: Track download error
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'csv_download_error', {
          calculator_type: 'professional',
          error_type: error instanceof Error ? error.message : 'Unknown error',
        });
      }

      // Show error notification
      notifications.show({
        title: 'Błąd',
        message: 'Nie udało się wygenerować pliku CSV',
        color: 'red',
        icon: <IconX size={18} />,
      });

    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      p="xl"
      shadow="sm"
      withBorder
      style={{
        borderColor: 'var(--mantine-color-green-3)',
        backgroundColor: 'var(--mantine-color-green-0)',
      }}
    >
      <Stack gap="md">
        {/* Header with Icon */}
        <Group gap="sm">
          <IconFileDownload size={28} color="var(--mantine-color-green-6)" />
          <Text size="xl" fw={600} c="green.8">
            Pobierz szczegółowe dane kalkulacji profesjonalnej
          </Text>
        </Group>

        {/* Description */}
        <Text size="sm" c="dimmed">
          Eksportuj godzinowe dane zużycia i kosztów dla wszystkich taryf w formacie CSV
          z uwzględnieniem miesięcznych zmian taryf i wzorców zużycia
        </Text>

        {/* Info Alert */}
        <Alert
          icon={<IconInfoCircle size={20} />}
          color="blue"
          variant="light"
          title="Zawartość pliku CSV"
        >
          <Stack gap="xs">
            <Text size="sm">
              Plik CSV zawiera szczegółowe dane dla każdej godziny roku:
            </Text>
            <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
              <li>
                <Text size="sm">Data, godzina, miesiąc, rodzaj dnia</Text>
              </li>
              <li>
                <Text size="sm">Zużycie energii (kWh) według wzorców miesięcznych</Text>
              </li>
              <li>
                <Text size="sm">
                  Koszty dla każdej taryfy (energia + dystrybucja) ze strefami czasowymi
                </Text>
              </li>
              <li>
                <Text size="sm">
                  Miesięczne różnice w taryfach i wzorcach godzinowych
                </Text>
              </li>
            </ul>
            <Text size="xs" c="dimmed" mt="xs">
              Format: 8760 wierszy (365 dni × 24 godziny) z kolumnami dla wszystkich 6 taryf
            </Text>
          </Stack>
        </Alert>

        {/* Security Warning */}
        <Alert
          icon={<IconAlertTriangle size={20} />}
          color="orange"
          variant="light"
          title="Bezpieczeństwo danych"
        >
          <Text size="sm">
            Pobrany plik CSV zawiera Twoje szczegółowe dane osobowe, miesięczne zużycie energii
            i parametry faktur. Przechowuj plik w bezpiecznym miejscu i nie udostępniaj go osobom trzecim.
            Jesteś odpowiedzialny/a za zabezpieczenie pobranego pliku.
          </Text>
        </Alert>

        {/* Export Button */}
        <Button
          leftSection={<IconFileDownload size={20} />}
          onClick={handleExport}
          loading={loading}
          disabled={loading}
          variant="filled"
          color="green"
          size="md"
          style={{ width: '100%', maxWidth: '300px' }}
        >
          Pobierz dane CSV
        </Button>

        {/* Additional info */}
        <Text size="xs" c="dimmed">
          Plik CSV można otworzyć w programie Excel, Google Sheets lub innej
          aplikacji do arkuszy kalkulacyjnych. Dane zawierają pełną roczną symulację
          z uwzględnieniem wszystkich miesięcznych konfiguracji.
        </Text>
      </Stack>
    </Paper>
  );
}
